import { t } from "@lingui/core/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect } from "react";
import type { SendCommitRequest } from "@/api";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { RampsText } from "@/components/ramps/Text";
import { getAdditionalInfoBackPage } from "@/hooks/constants/ramps-flow";
import type { RampsPage } from "@/hooks/types/paths.models";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useAnalytics } from "@/hooks/usePostHog";
import { navigateWithRampsState } from "@/lib/ramps-navigation";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { toast } from "@/lib/toast";
import { rampsStore } from "@/stores/ramps-store";
import { rampsActions, useRampsStore } from "@/stores/use-ramps-store";
import type { GFFPFieldInfo } from "@/types/additional-info.models";
import { buildSendCommitPayload } from "@/utils/transactionPayloadBuilder";
import { isDataFromTrustedSource } from "@/utils/trustedSource";
import { Progress } from "../components/ui/progress";

function AdditionalInfoPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const store = useRampsStore();
	const { trackPageView } = useAnalytics();

	// Back navigation target depends on how the user reached additional-info:
	// - Prefilled / trusted-source data (partner wallet via SEP-9): the manual
	//   entry pages were skipped and the user came straight to review, so going
	//   "back" should return them to the calculator (the editable entry point).
	// - Normal form flow (first-time user): step back to the previous page in
	//   the flow they actually filled out.
	// We intentionally do NOT use browser history here: additional-info is always
	// reached from review, so history.back() would land on review in both cases.
	const handleBack = useCallback(() => {
		const target: RampsPage = getAdditionalInfoBackPage(
			isDataFromTrustedSource(rampsStore.state, search),
			store.operation,
		);

		navigate(
			navigateWithRampsState(search, {
				to: `/ramps/${target}` as const,
				search: { step: target },
			}),
		);
	}, [navigate, search, store.operation]);
	// Get auth token (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;

	// Track page view
	useEffect(() => {
		trackPageView("additional-info", { page_name: "additional-info" });
	}, [trackPageView]);
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Extract fields once
	const offendingFields =
		(store.transactionResponse?.errors?.[0]
			?.offendingFields as GFFPFieldInfo[]) || [];

	const formatFieldsWithDotNotation = (fields: Record<string, unknown>) => {
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(fields)) {
			// Convert dashes to dots in the key
			const dotNotationKey = key.replace(/-/g, ".");
			result[dotNotationKey] = value;
		}

		return result;
	};

	const formatFieldsToDeeplyNested = (formData: Record<string, unknown>) => {
		const dotNotatedData = formatFieldsWithDotNotation(formData);
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(dotNotatedData)) {
			// Split the key by dots to create the nested path
			const keys = key.split(".");
			let current = result;

			// Navigate/create the nested structure
			for (let i = 0; i < keys.length - 1; i++) {
				const currentKey = keys[i];
				if (!(currentKey in current)) {
					current[currentKey] = {};
				}
				current = current[currentKey] as Record<string, unknown>;
			}

			// Set the final value
			const finalKey = keys[keys.length - 1];
			current[finalKey] = value;
		}

		return result;
	};

	const deepMerge = (
		target: Record<string, unknown>,
		source: Record<string, unknown>,
	): Record<string, unknown> => {
		const result = { ...target };

		for (const [key, value] of Object.entries(source)) {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				// If both target and source have objects at this key, merge them recursively
				if (
					result[key] &&
					typeof result[key] === "object" &&
					!Array.isArray(result[key])
				) {
					result[key] = deepMerge(
						result[key] as Record<string, unknown>,
						value as Record<string, unknown>,
					);
				} else {
					// Otherwise, use the source value
					result[key] = value;
				}
			} else {
				// For primitive values, arrays, or null, use the source value
				result[key] = value;
			}
		}

		return result;
	};

	const addAdditionalFieldsToValidatePayload = (
		formattedFormData: Record<string, unknown>,
		validatePayload: SendCommitRequest,
	) => {
		if (!validatePayload) {
			return formattedFormData as unknown as SendCommitRequest;
		}

		return deepMerge(
			validatePayload as unknown as Record<string, unknown>,
			formattedFormData,
		) as unknown as SendCommitRequest;
	};

	const handleFormSubmit = async (_data: Record<string, unknown>) => {
		if (!_data) return;

		// Base payload: prefer the payload validated at review. If it's missing
		// (e.g. the user arrived via an edit-revalidation path that didn't persist
		// it), rebuild it from the store so we never send an incomplete request.
		// This was a silent no-op path, most visible with SEP-9 prefilled data.
		const basePayload = store.validatePayload ?? buildSendCommitPayload(store);

		const formattedFormData = formatFieldsToDeeplyNested(_data);

		const updatedPayload = addAdditionalFieldsToValidatePayload(
			formattedFormData,
			basePayload,
		);

		rampsActions.setValidatePayload(updatedPayload);

		// Determine operation: use store.operation if set, otherwise infer from cashInLocation
		const operation =
			store.operation || (store.cashInLocation ? "deposit" : "withdraw");

		try {
			const validationResponse = await apiClient.sendCommit.validateTransaction(
				store.transactionId,
				updatedPayload,
				{ params: { operation } },
			);

			rampsActions.setTransactionResponse(validationResponse);

			if (validationResponse.readyForCommit) {
				navigate(
					navigateWithRampsState(search, {
						to: "/ramps/review",
						search: { step: "review" },
					}),
				);
			} else if (
				validationResponse.errors &&
				validationResponse.errors.length > 0
			) {
				// Show user-friendly error message
				toast.error(
					validationResponse.errors[0].message ||
						t`Validation failed. Please check your information.`,
					{ header: t`Validation Error` },
				);
			} else {
				// Generic error if no specific error details
				toast.error(
					t`Unable to validate transaction. Please check your information and try again.`,
					{ header: t`Validation Failed` },
				);
			}
		} catch (_error) {
			// The validate request can reject (e.g. 5xx / network). Previously the
			// rejection was swallowed and the user saw nothing happen — the root
			// cause of the intermittent "Continue does nothing" report.
			toast.error(
				t`Unable to validate transaction. Please check your connection and try again.`,
				{ header: t`Validation Failed` },
			);
		}
	};

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={95}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Additional Information Required`}
					</RampsText>
					<div className="text-muted-foreground text-sm">
						{t`Please provide the following additional information to complete your transaction.`}
					</div>
				</div>

				{offendingFields.length === 0 ? (
					<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
						<RampsText variant="body-default" color="secondary">
							{t`Loading additional fields...`}
						</RampsText>
					</div>
				) : (
					<DynamicForm
						fields={offendingFields}
						onSubmit={handleFormSubmit}
						className="bg-card"
					/>
				)}
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/additional-info")({
	component: AdditionalInfoPage,
});
